import supabase from '../supabaseClient';

// Use relative proxy for payments server (legacy)
const API_BASE = process.env.REACT_APP_STRIPE_SERVER_URL || '/payments';

export async function getUserPlans() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { chatbot: { active: false, plan: 'free' }, anomaly: { active: false, plan: 'free' } };

  const base = { chatbot: { active: false, plan: 'free' }, anomaly: { active: false, plan: 'free' } };

  // Prefer Stripe-backed subscription view for accurate trial/end dates
  try {
    const headers = await authHeader();
    const resp = await fetch(`${API_BASE}/subscriptions`, { headers });
    if (resp.ok) {
      const data = await resp.json();
      const subs = data?.subscriptions || [];
      const now = Date.now();

      subs.forEach((sub) => {
        const productNames = Array.isArray(sub.product_names) ? sub.product_names : [];
        const status = (sub.status || '').toLowerCase();
        const isTrial = status === 'trialing';
        const isActiveStatus = status === 'active' || status === 'trialing';
        let endMs = null;
        if (typeof sub.current_period_end === 'number') {
          endMs = sub.current_period_end * 1000;
        } else if (sub.current_period_end) {
          const parsed = Date.parse(sub.current_period_end);
          endMs = Number.isNaN(parsed) ? null : parsed;
        }
        const stillWithin = endMs ? now < endMs : false;
        const isActive = isActiveStatus || (sub.cancel_at_period_end && stillWithin);
        const plan = isTrial ? 'trial' : (isActive ? 'pro' : 'free');

        productNames.forEach((name) => {
          if (!name) return;
          const prev = base[name] || { active: false, plan: 'free' };
          const nextActive = prev.active || isActive;
          let nextPlan = prev.plan;
          if (nextActive) {
            nextPlan = (prev.plan === 'trial' || plan === 'trial') ? 'trial' : 'pro';
          } else {
            nextPlan = 'free';
          }
          base[name] = { active: nextActive, plan: nextPlan };
        });
      });

      return base;
    }
  } catch (err) {
    console.warn('getUserPlans: Stripe-backed subscriptions failed, falling back to Supabase:', err);
  }

  const { data, error } = await supabase
    .from('user_products')
    .select('product_name, plan, is_active')
    .eq('user_id', user.id);

  if (error) return base;

  data?.forEach(row => {
    base[row.product_name] = { active: !!row.is_active && row.plan !== 'free', plan: row.plan || 'free' };
  });
  return base;
}

export function hasPlan(plans, productName) {
  return !!plans?.[productName]?.active;
}

export function ensureLoggedIn(navigate, location) {
  // This function should be used with AuthContext to check if user is logged in
  // If no user, redirect to login with current path as next parameter
  const currentPath = location.pathname + location.search;
  const nextParam = encodeURIComponent(currentPath);
  navigate(`/login?next=${nextParam}`);
}

export function mustOwnProduct(productName, plans, navigate) {
  if (!hasPlan(plans, productName)) {
    navigate(`/price?need=${productName}`);
    return false;
  }
  return true;
}

// --- New: Subscription management API helpers ---
async function authHeader(explicitToken) {
  // Always prefer the freshest session token available
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token || explicitToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (err) {
    // If session fetch fails, fall back to explicit token (if provided)
    return explicitToken ? { Authorization: `Bearer ${explicitToken}` } : {};
  }
}

export async function fetchSubscriptions(accessToken) {
  let headers = { 'Content-Type': 'application/json', ...(await authHeader(accessToken)) };

  // Prefer server-side endpoint (uses service key, bypasses RLS if needed)
  try {
    const resp = await fetch(`${API_BASE}/subscriptions`, { headers });
    if (resp.ok) return resp.json();
    // Retry once on auth errors (token may be stale)
    if (resp.status === 401 || resp.status === 403) {
      const refreshed = await supabase.auth.refreshSession().catch(() => null);
      const freshToken = refreshed?.data?.session?.access_token;
      headers = { 'Content-Type': 'application/json', ...(await authHeader(freshToken || accessToken)) };
      const retryResp = await fetch(`${API_BASE}/subscriptions`, { headers });
      if (retryResp.ok) return retryResp.json();
    }
  } catch (err) {
    console.warn('Subscriptions API call failed, falling back to Supabase:', err);
  }

  // Fallback: direct Supabase query (works when RLS allows)
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Nicht eingeloggt');

  const { data, error } = await supabase
    .from('user_products')
    .select('product_name, plan, is_active, stripe_subscription_id, created_at')
    .eq('user_id', user.id);

  if (error) {
    console.error('Supabase subscription load error:', error);
    throw new Error('Abos konnten nicht geladen werden');
  }

  const subscriptions = (data || []).map((row, idx) => ({
    id: row.stripe_subscription_id || `${row.product_name}-${idx}`,
    product_names: [row.product_name || 'Abo'],
    status: row.is_active ? 'active' : 'inactive',
    cancel_at_period_end: false,
    start_date: row.created_at,
    current_period_end: row.created_at,
  }));

  return { subscriptions };
}

export async function cancelSubscription(subscriptionId, accessToken) {
  const headers = { 'Content-Type': 'application/json', ...(await authHeader(accessToken)) };
  const resp = await fetch(`${API_BASE}/subscription/cancel`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ subscription_id: subscriptionId })
  });
  if (!resp.ok) throw new Error('Failed to cancel subscription');
  return resp.json();
}

export async function resumeSubscription(subscriptionId, accessToken) {
  const headers = { 'Content-Type': 'application/json', ...(await authHeader(accessToken)) };
  const resp = await fetch(`${API_BASE}/subscription/resume`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ subscription_id: subscriptionId })
  });
  if (!resp.ok) throw new Error('Failed to resume subscription');
  return resp.json();
}

export async function openBillingPortal(accessToken) {
  const headers = { 'Content-Type': 'application/json', ...(await authHeader(accessToken)) };
  const resp = await fetch(`${API_BASE}/billing-portal`, { method: 'POST', headers });
  if (!resp.ok) throw new Error('Failed to create billing portal session');
  const { url } = await resp.json();
  window.location.href = url;
}
